export const capitalizeFirstLetter = (str) => {
  if (!str) return ""; // Handle empty or null values
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper to get only changed fields
export const getChangedFields = (initial, current) => {
  const changed = {};
  for (const key in current) {
    if (
      typeof current[key] === "object" &&
      current[key] !== null &&
      !Array.isArray(current[key])
    ) {
      const nested = getChangedFields(initial[key] || {}, current[key]);
      if (Object.keys(nested).length > 0) changed[key] = nested;
    } else if (Array.isArray(current[key])) {
      // For arrays, do a shallow compare
      if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
        changed[key] = current[key];
      }
    } else {
      if (current[key] !== initial[key]) {
        changed[key] = current[key];
      }
    }
  }
  return changed;
};

export const getOcpiRole = (roles, roleId) => {
  const role = roles.find((r) => r.id === Number(roleId));
  return role ? role.value : "Unknown Role";
};

export const getOcpiVersions = (ocpiVersions, versions) => {
  const v = ocpiVersions
    .filter((value) => versions.includes("" + value.id))
    .map((value) => value.value)
    .join(", ");
  return v || "Unknown Versions";
};

export const getStartDateFromInterval = (interval) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (interval) {
    case "Today":
      return today;
    
    case "Yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    
    case "Last 7 Days":
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return sevenDaysAgo;
    
    case "Last 30 Days":
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return thirtyDaysAgo;
    
    case "Last 90 Days":
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      return ninetyDaysAgo;
    
    case "This Month":
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return thisMonthStart;
    
    case "Last Month":
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return lastMonthStart;
    
    case "This Quarter":
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
      return thisQuarterStart;
    
    case "Last Quarter":
      const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
      let lastQuarterYear = today.getFullYear();
      let lastQuarterMonth = lastQuarter * 3;
      
      if (lastQuarter < 0) {
        lastQuarterYear--;
        lastQuarterMonth = 9; // Q4 of previous year (October = month 9)
      }
      
      const lastQuarterStart = new Date(lastQuarterYear, lastQuarterMonth, 1);
      return lastQuarterStart;
    
    case "This Year":
      const thisYearStart = new Date(today.getFullYear(), 0, 1);
      return thisYearStart;
    
    case "Last Year":
      const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
      return lastYearStart;
    default:
      return today;
  }
};

/**
 * Debounce function
 * @param {function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {function} Debounced function
 */
export const debounceFunc = (func, delay = 400) => {
  let timerId;
  return (...args) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      func.apply(null, args);
    }, delay);
  };
};

/**
 * Builds a query string for session-related API requests.
 * @param {Object} params - Key-value pairs to be included as query parameters.
 * @returns {string} Query string starting with '?' or an empty string if no params.
 */

export const buildQuery = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  return queryParams.toString() ? `?${queryParams.toString()}` : '';
};